import React from 'react'
import { Users, LineChart, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react'
import { fetchAdminOverview, fetchAdminUsersPage, syncSequenzySubscribers, type AdminUserWithUsageDto, type AdminUsersPageResponse } from '../api/admin'
import { useQuery, useMutation } from '@tanstack/react-query'
import Button from '../components/ui/button'
import { useAuth } from '../hooks/use-auth'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import TooltipComponent from '../components/ui/tooltip'

const StatCard: React.FC<{ label: string; value: string | number; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="rounded-xl border border-secondary-200 bg-background p-4">
    <div className="flex items-center justify-between">
      <p className="text-sm text-foreground/60">{label}</p>
      {icon}
    </div>
    <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
  </div>
)

const UsersTable: React.FC<{ users: AdminUserWithUsageDto[] }> = ({ users }) => {
  return (
    <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-background">
      <table className="w-full text-sm">
        <thead className="text-left text-foreground/60">
          <tr>
            <th className="px-4 py-3">User</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3">Verified</th>
            <th className="px-4 py-3">Joined</th>
            <th className="px-4 py-3">Spent</th>
            <th className="px-4 py-3">Total lines</th>
            <th className="px-4 py-3">Usage</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} className="border-t border-secondary-100">
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">{u.name}</span>
                  <span className="text-xs text-foreground/60">{u.email}</span>
                </div>
              </td>
              <td className="px-4 py-3">{u.plan}</td>
              <td className="px-4 py-3">
                {u.emailVerified ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success-50 text-success-700 ring-1 ring-inset ring-success-200 px-2 py-1">
                    <CheckCircle className="h-3 w-3" /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-200 px-2 py-1">
                    <XCircle className="h-3 w-3" /> Unverified
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <TooltipComponent content={new Date(u.joinedAt).toLocaleString()}>
                  <span>{new Date(u.joinedAt).toLocaleDateString()}</span>
                </TooltipComponent>
              </td>
              <td className="px-4 py-3">{`$${u.totalSpent.toFixed(2)}`}</td>
              <td className="px-4 py-3">{u.totalLines}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-secondary-50 text-secondary-700 ring-1 ring-inset ring-secondary-200 px-2 py-1">All {u.totalPodcasts}</span>
                  <span className="rounded-full bg-secondary-50 text-secondary-700 ring-1 ring-inset ring-secondary-200 px-2 py-1">Draft {u.draftPodcasts}</span>
                  <span className="rounded-full bg-info-50 text-info-700 ring-1 ring-inset ring-info-200 px-2 py-1">Processing {u.processingPodcasts}</span>
                  <span className="rounded-full bg-success-50 text-success-700 ring-1 ring-inset ring-success-200 px-2 py-1">Done {u.completedPodcasts}</span>
                  <span className="rounded-full bg-danger-50 text-danger-700 ring-1 ring-inset ring-danger-200 px-2 py-1">Failed {u.failedPodcasts}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const RegistrationsChart: React.FC<{ data: Array<{ date: string; count: number }> }> = ({ data }) => {
  // Format data for chart
  const chartData = data.map(d => ({
    label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count: d.count
  }))

  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <defs>
            <linearGradient id="regGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-brand-500)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--color-brand-500)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-secondary-200)" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'rgba(23,23,23,0.6)' }} axisLine={false} tickLine={false} interval={4} />
          <YAxis tick={{ fontSize: 11, fill: 'rgba(23,23,23,0.6)' }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid var(--color-secondary-200)' }} labelStyle={{ color: 'var(--color-foreground)' }} formatter={(v: number) => [v, 'Registrations']} />
          <Area type="monotone" dataKey="count" stroke="var(--color-brand-600)" strokeWidth={2} fill="url(#regGradient)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

const ConversionChart: React.FC<{ 
  data: Array<{ date: string; count: number }>
  title: string
  color: string
  gradientId: string
}> = ({ data, title, color, gradientId }) => {
  // Format data for chart
  const chartData = data.map(d => ({
    label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    rate: d.count
  }))

  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-secondary-200)" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'rgba(23,23,23,0.6)' }} axisLine={false} tickLine={false} interval={4} />
          <YAxis 
            tick={{ fontSize: 11, fill: 'rgba(23,23,23,0.6)' }} 
            axisLine={false} 
            tickLine={false} 
            width={35} 
            allowDecimals={false}
            domain={[0, 100]}
          />
          <Tooltip 
            contentStyle={{ borderRadius: 12, border: '1px solid var(--color-secondary-200)' }} 
            labelStyle={{ color: 'var(--color-foreground)' }} 
            formatter={(v: number) => [`${v}%`, title]} 
          />
          <Area type="monotone" dataKey="rate" stroke={color} strokeWidth={2} fill={`url(#${gradientId})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

const AdminUsersPage: React.FC = () => {
  const { user } = useAuth()
  const [page, setPage] = React.useState(1)
  const pageSize = 20

  const sequenzySyncMutation = useMutation({
    mutationFn: syncSequenzySubscribers,
  })

  const overviewQuery = useQuery({
    queryKey: ['admin', 'overview'] as const,
    queryFn: fetchAdminOverview,
    enabled: !!user?.isAdmin,
    staleTime: 60_000,
  })

  const usersQuery = useQuery<AdminUsersPageResponse, Error, AdminUsersPageResponse, [string, string, number, number]>({
    queryKey: ['admin', 'users', page, pageSize],
    queryFn: () => fetchAdminUsersPage(page, pageSize),
    enabled: !!user?.isAdmin,
    placeholderData: (prev) => prev,
  })

  if (!user?.isAdmin) {
    return (
      <div className="rounded-xl border border-secondary-200 bg-background p-6">
        <p className="text-foreground">You do not have access to this page.</p>
      </div>
    )
  }

  const dailyCounts = overviewQuery.data?.registrationsHistory ?? []
  const today = new Date().toISOString().slice(0,10)
  const todayCount = dailyCounts.find(d => d.date.slice(0,10) === today)?.count ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Admin Users</h1>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => sequenzySyncMutation.mutate()}
          disabled={sequenzySyncMutation.isPending}
          className="cursor-pointer"
        >
          {sequenzySyncMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Sync to Sequenzy
        </Button>
      </div>

      {/* Sequenzy sync result */}
      {sequenzySyncMutation.isSuccess && (
        <div className="rounded-xl border border-success-200 bg-success-50 p-4 text-sm text-success-700">
          Synced {sequenzySyncMutation.data.syncedCount} of {sequenzySyncMutation.data.totalUsers} users to Sequenzy.
          {sequenzySyncMutation.data.skippedCount > 0 && ` Skipped ${sequenzySyncMutation.data.skippedCount}.`}
          {sequenzySyncMutation.data.failedCount > 0 && ` Failed ${sequenzySyncMutation.data.failedCount}.`}
        </div>
      )}
      {sequenzySyncMutation.isError && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
          Failed to sync: {(sequenzySyncMutation.error as Error).message}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Users" value={overviewQuery.data?.totalUsers ?? '—'} icon={<Users className="h-4 w-4 text-foreground/40" />} />
        <StatCard label="Paid users" value={overviewQuery.data?.paidUsers ?? '—'} />
        <StatCard label="New subs (30d)" value={overviewQuery.data?.newSubscriptionsCount ?? '—'} />
        <StatCard label="Transactions (30d)" value={overviewQuery.data?.newTransactionsCount ?? '—'} />
      </div>

      {/* Conversion metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard 
          label="Signup → Created" 
          value={overviewQuery.data?.signupToCreatedConversionRate ? `${overviewQuery.data.signupToCreatedConversionRate.toFixed(1)}%` : '—'} 
        />
        <StatCard 
          label="Signup → Completed" 
          value={overviewQuery.data?.signupToCompletedConversionRate ? `${overviewQuery.data.signupToCompletedConversionRate.toFixed(1)}%` : '—'} 
        />
        <StatCard 
          label="Created → Completed" 
          value={overviewQuery.data?.createdToCompletedConversionRate ? `${overviewQuery.data.createdToCompletedConversionRate.toFixed(1)}%` : '—'} 
        />
      </div>

      {/* Conversion Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Signup to Created Conversion */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-foreground">Signup → Created Conversion</h2>
            <span className="text-sm text-foreground/60">
              Current: {overviewQuery.data?.signupToCreatedConversionRate ? `${overviewQuery.data.signupToCreatedConversionRate.toFixed(1)}%` : '—'}
            </span>
          </div>
          <div className="rounded-xl border border-secondary-200 bg-background p-4">
            <div className="flex items-center gap-2 mb-3">
              <LineChart className="h-4 w-4 text-foreground/40" />
              <span className="text-sm font-medium text-foreground">Last 30 days</span>
            </div>
            <div className="mt-3">
              {overviewQuery.isLoading ? (
                <div className="h-40 rounded bg-secondary-50 animate-pulse" />
              ) : (
                <ConversionChart 
                  data={overviewQuery.data?.signupToCreatedHistory ?? []}
                  title="Signup → Created"
                  color="var(--color-info-500)"
                  gradientId="signupToCreatedGradient"
                />
              )}
            </div>
          </div>
        </div>

        {/* Created to Completed Conversion */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-foreground">Created → Completed Conversion</h2>
            <span className="text-sm text-foreground/60">
              Current: {overviewQuery.data?.createdToCompletedConversionRate ? `${overviewQuery.data.createdToCompletedConversionRate.toFixed(1)}%` : '—'}
            </span>
          </div>
          <div className="rounded-xl border border-secondary-200 bg-background p-4">
            <div className="flex items-center gap-2 mb-3">
              <LineChart className="h-4 w-4 text-foreground/40" />
              <span className="text-sm font-medium text-foreground">Last 30 days</span>
            </div>
            <div className="mt-3">
              {overviewQuery.isLoading ? (
                <div className="h-40 rounded bg-secondary-50 animate-pulse" />
              ) : (
                <ConversionChart 
                  data={overviewQuery.data?.createdToCompletedHistory ?? []}
                  title="Created → Completed"
                  color="var(--color-success-500)"
                  gradientId="createdToCompletedGradient"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Registration metrics */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-foreground">User Registrations</h2>
          <span className="text-sm text-foreground/60">Today: {todayCount}</span>
        </div>
        <div className="rounded-xl border border-secondary-200 bg-background p-4">
          <div className="flex items-center gap-2 mb-3">
            <LineChart className="h-4 w-4 text-foreground/40" />
            <span className="text-sm font-medium text-foreground">Last 30 days</span>
          </div>
          <div className="mt-3">
            {overviewQuery.isLoading ? (
              <div className="h-40 rounded bg-secondary-50 animate-pulse" />
            ) : (
              <RegistrationsChart data={dailyCounts.map(d => ({ date: d.date, count: d.count }))} />
            )}
          </div>
        </div>
      </div>

      {/* Users table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Users</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={page === 1 || usersQuery.isLoading}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="cursor-pointer"
            >Prev</Button>
            <span className="text-sm text-foreground/60">Page {page}</span>
            <Button
              variant="ghost"
              size="sm"
              disabled={usersQuery.isLoading || ((usersQuery.data?.page || 1) * (usersQuery.data?.pageSize || pageSize) >= (usersQuery.data?.totalUsers || 0))}
              onClick={() => setPage(p => p + 1)}
              className="cursor-pointer"
            >Next</Button>
          </div>
        </div>
        {usersQuery.isLoading ? (
          <div className="h-40 rounded-xl border border-secondary-200 bg-background grid place-items-center text-foreground/60">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <UsersTable users={usersQuery.data?.users ?? []} />
        )}
      </div>
    </div>
  )
}

export default AdminUsersPage